import { isEqual } from 'lodash';
import { createSelector } from 'reselect';

import type { StateTree } from '@makemydeal/dr-dash-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { CASH, FINANCE } from '@makemydeal/dr-platform-types';
import type { AppStateComplete } from '@makemydeal/dr-shared-store';
import { dealerSelectors, featureToggleSelectors, sharedBootstrapSelectors } from '@makemydeal/dr-shared-store';
import type { RealState } from '@makemydeal/shared-scoped-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';

import * as dealModeSelectors from './dealModeSelectors';
import * as offerReduxSelectors from './offerRedux';
import type { ScenarioGridCell, ScenarioGridRowKey, ScenarioRowContext } from '../types/scenarioGridTypes';
import { SCENARIO_GRID_ROWS, SCENARIO_GRID_ROWS_BY_KEY } from '../utils/scenarioGridRows';

type ScopedRealState = AppStateComplete<RealState, StateTree>;

export const isScenarioGridActive = (state: StateTree): boolean =>
    sharedBootstrapSelectors.wasBootstrapSuccessful(state) &&
    featureToggleSelectors.isMvRedesignEnabled(state) &&
    dealerSelectors.isDealCentralExperience(state) &&
    !dealerSelectors.isMvLiteEnabled(state) &&
    !dealModeSelectors.isFnIDealMode(state);

export const isFlexBuyDeal = (state: StateTree): boolean =>
    offerReduxSelectors.getCurrentOfferType(state) === FINANCE && offerReduxSelectors.isFlexBuyPayment(state);

const isPaymentGridOn = (state: StateTree, offerType: OfferType, isFlexBuy: boolean): boolean => {
    if (offerType === CASH) {
        return false;
    }
    return isFlexBuy
        ? offerReduxSelectors.getFlexBuyPaymentGridToggle(state)
        : offerReduxSelectors.getPaymentGridToggle(state, offerType);
};

export const getScenarioRowContext = (state: StateTree): ScenarioRowContext => {
    const offerType = offerReduxSelectors.getCurrentOfferType(state);
    const isFlexBuy = isFlexBuyDeal(state);

    return {
        offerType,
        isFlexBuy,
        isEquityAllocationOrderEnabled: featureToggleSelectors.isEquityAllocationOrderEnabled(state),
        isLeaseContractFieldsEnabled: featureToggleSelectors.isEnableLeaseContractFields(state),
        isOnePayLeaseEnabled: featureToggleSelectors.enableOnePayLease_F231960(state),
        isBalloonEnabledForDealer: dealerSelectors.isBalloonDealTypeEnabledForDealer(state),
        isDueAtSigningEnhancementsEnabled: featureToggleSelectors.enableDueAtSigningEnhancements(state),
        hasNetCashPayout: offerReduxSelectors.getNetCashPayout(state) > 0,
        isPaymentGridOn: isPaymentGridOn(state, offerType, isFlexBuy)
    };
};

export const getScenarioRowKeys = createSelector(
    [(state: ScopedRealState) => state],
    (state): ScenarioGridRowKey[] => {
        const contexts = scopedStateSelectors
            .getInitializedScopesOrder(state)
            .map((scopeId) => getScenarioRowContext(scopedStateSelectors.getScopeStateById(state, scopeId) as StateTree));

        return SCENARIO_GRID_ROWS.filter((row) => contexts.some(row.appliesTo)).map((row) => row.key);
    },
    { memoizeOptions: { resultEqualityCheck: isEqual } }
);

const getCurrentScopeRowContext = createSelector(
    [(state: ScopedRealState) => state],
    (state) => getScenarioRowContext(state as StateTree),
    { memoizeOptions: { resultEqualityCheck: isEqual } }
);

export const getScenarioGridCells = createSelector(
    [getScenarioRowKeys, getCurrentScopeRowContext],
    (rowKeys, context): ScenarioGridCell[] =>
        rowKeys.map((rowKey) => {
            const row = SCENARIO_GRID_ROWS_BY_KEY[rowKey];
            return { rowKey, label: row.label, isApplicable: row.appliesTo(context) };
        })
);
